// Registre des courriers : liste filtrable, tri, pagination, export CSV du registre (L0-C).
import { LIBELLES_CONFIDENTIALITE, LIBELLES_ETATS_COURRIER, type CourrierResume } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, Champ, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Selection, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Download, Mail, Plus } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { moduleActif, peut } from '../../session/permissions';
import { telechargerBlob } from './csv';
import { useListePaginee } from './listes';
import commun from '../../commun/commun.module.css';

export function puceCourrier(c: Pick<CourrierResume, 'etat' | 'en_retard'>) {
  if (c.en_retard) return <PuceEtat ton="retard" libelle="En retard" />;
  const libelle = LIBELLES_ETATS_COURRIER[c.etat] ?? c.etat;
  const ton = c.etat === 'valide' ? 'signer' : ['repondu', 'classe', 'envoye', 'archive', 'signe'].includes(c.etat) ? 'ok' : c.etat === 'enregistre' ? 'attente' : 'neutre';
  return <PuceEtat ton={ton} libelle={c.etat === 'valide' ? 'À signer' : libelle} />;
}

export function PageCourriers() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('courriers', { sens: '', etat: '' });
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  const liste = useListePaginee<CourrierResume>('courriers', '/courriers', { sens: filtres.sens ?? '', etat: filtres.etat ?? '', q: recherche });

  const exporter = async () => {
    setErreurExport(null);
    setExportEnCours(true);
    try {
      const r = await requete<Response>('/courriers/registre', { brut: true, parametres: { sens: filtres.sens || 'arrivee', annee } });
      telechargerBlob(`registre-courriers-${filtres.sens || 'arrivee'}-${annee}.csv`, await r.blob());
    } catch (e) {
      setErreurExport(messageErreur(e));
    } finally {
      setExportEnCours(false);
    }
  };

  const colonnes: ColonneTableau<CourrierResume>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (c) => c.numero, cellule: (c) => <Lien href={`/courriers/${c.id}`} className={commun.reference}>{c.numero ?? 'sans numéro'}</Lien> },
    { id: 'sens', entete: 'Sens', cellule: (c) => (c.sens === 'arrivee' ? 'Arrivée' : 'Départ'), valeurTri: (c) => c.sens },
    { id: 'objet', entete: 'Objet', valeurTri: (c) => c.objet, cellule: (c) => <>{c.objet}{c.confidentialite !== 'ordinaire' ? <> <PuceEtat ton="neutre" libelle={LIBELLES_CONFIDENTIALITE[c.confidentialite] ?? c.confidentialite} /></> : null}</> },
    { id: 'correspondant', entete: 'Correspondant', cellule: (c) => c.correspondant_nom, valeurTri: (c) => c.correspondant_nom },
    { id: 'service', entete: 'Service', cellule: (c) => c.service_affectataire?.nom ?? 'non affecté' },
    { id: 'date', entete: 'Enregistré le', cellule: (c) => date(c.date_enregistrement), valeurTri: (c) => c.date_enregistrement },
    { id: 'echeance', entete: 'Échéance', cellule: (c) => date(c.echeance_interne), valeurTri: (c) => c.echeance_interne ?? null },
    { id: 'etat', entete: 'État', cellule: puceCourrier, valeurTri: (c) => c.etat },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration générale"
        titre="Courriers"
        description="Registre des courriers arrivée et départ de la collectivité."
        actions={peut(contexte, 'courrier', 'creer') && moduleActif(contexte, 'M02') !== false ? <LienBouton href="/courriers/nouveau" variante="principal" icone={Plus}>Enregistrer un courrier</LienBouton> : null}
      />
      <Filtres
        groupes={[
          { id: 'sens', libelle: 'Sens', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'arrivee', libelle: 'Arrivée' }, { valeur: 'depart', libelle: 'Départ' }] },
          {
            id: 'etat',
            libelle: 'État',
            options: [
              { valeur: '', libelle: 'Tous' },
              { valeur: 'enregistre', libelle: 'Enregistrés' },
              { valeur: 'affecte', libelle: 'Affectés' },
              { valeur: 'en_traitement', libelle: 'En traitement' },
              { valeur: 'valide', libelle: 'À signer' },
              { valeur: 'envoye', libelle: 'Envoyés' },
            ],
          },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <form className={commun.ligne} style={{ alignItems: 'flex-end' }} role="search" onSubmit={(e) => { e.preventDefault(); setRecherche(q.trim()); }}>
        <Champ libelle="Rechercher dans l'objet ou le correspondant" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        <Bouton type="submit" variante="secondaire">Rechercher</Bouton>
        {peut(contexte, 'courrier', 'exporter') ? (
          <>
            <Selection libelle="Année du registre" value={annee} onChange={(e) => setAnnee(e.target.value)} options={[0, 1, 2].map((i) => String(new Date().getFullYear() - i)).map((a) => ({ valeur: a, libelle: a }))} />
            <Bouton variante="secondaire" icone={Download} chargement={exportEnCours} onClick={() => void exporter()}>Exporter le registre (CSV)</Bouton>
          </>
        ) : null}
      </form>
      {erreurExport ? <Bandeau type="erreur">Export impossible : {erreurExport}</Bandeau> : null}
      <EtatRequete chargement={liste.requete.isPending} erreur={liste.requete.error} onReessayer={() => void liste.requete.refetch()} lot="L0-C" fonction="Le registre des courriers">
        {liste.elements.length === 0 ? (
          <EtatVide titre="Aucun courrier" icone={Mail}>
            <p>Aucun courrier ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Registre des courriers" colonnes={colonnes} lignes={liste.elements} cleLigne={(c) => c.id} cleColonnes="courriers" pagination={liste.pagination} />
        )}
      </EtatRequete>
    </>
  );
}
